import React, { useState, useEffect } from 'react';
import { fetchCatalog } from '../../services/CatalogService';
import AlbumGrid from './AlbumGrid';
import TrackGrid from './TrackGrid';
import FilterSidebar from './FilterSidebar';
import './CatalogPanel.css';

// Static album data based on the catalog
const albumsData = [
  {
    title: "Okina Sakana",
    artist: "Rue de Vivre",
    cover: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/okina-sakana/art/okina-cover-2.png",
    releaseDate: "2024",
    spotify_album_url: "https://open.spotify.com/album/6hCajqjMcAwptBiNc7IQEM",
    ascap_work_id: "930467502",
    tracks: [
      { id: "okina-01", track_title: "Monday Grind", duration: "3:23" },
      { id: "okina-02", track_title: "Hump Day", duration: "3:22" },
      { id: "okina-03", track_title: "Friday Flex", duration: "3:17" },
      { id: "okina-04", track_title: "Big Fish (Okina Sakana)", duration: "3:09" },
      { id: "okina-05", track_title: "Weed", duration: "2:54" },
      { id: "okina-06", track_title: "999,999", duration: "2:12" },
      { id: "okina-07", track_title: "Shine Every Time (Carpe Diem)", duration: "3:31" },
      { id: "okina-08", track_title: "OG Bashment", duration: "3:27" },
      { id: "okina-09", track_title: "You're the Shift (Unreleased)", duration: "3:18" }
    ]
  },
  {
    title: "Reclaim",
    artist: "Rue de Vivre", 
    cover: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/reclaim/art/reclaim-cover.png",
    releaseDate: "2025",
    spotify_album_url: "https://open.spotify.com/album/3mza84ri2Y2GqRLpdYqMjE",
    ascap_work_id: "930467503",
    tracks: [
      { id: "reclaim-01", track_title: "Reclaim (Track 1)", duration: "3:45" },
      { id: "reclaim-02", track_title: "Reclaim (Track 2)", duration: "4:12" },
      { id: "reclaim-03", track_title: "Reclaim (Track 3)", duration: "3:28" },
      { id: "reclaim-04", track_title: "Reclaim (Track 4)", duration: "4:01" },
      { id: "reclaim-05", track_title: "Reclaim (Track 5)", duration: "3:33" },
      { id: "reclaim-06", track_title: "Reclaim (Track 6)", duration: "4:18" },
      { id: "reclaim-07", track_title: "Reclaim (Track 7)", duration: "3:52" },
      { id: "reclaim-08", track_title: "Reclaim (Track 8)", duration: "4:05" },
      { id: "reclaim-09", track_title: "Reclaim (Track 9)", duration: "3:41" },
      { id: "reclaim-10", track_title: "Reclaim (Track 10)", duration: "4:23" },
      { id: "reclaim-11", track_title: "Reclaim (Track 11)", duration: "3:56" },
      { id: "reclaim-12", track_title: "Reclaim (Track 12)", duration: "4:14" },
      { id: "reclaim-13", track_title: "Reclaim (Track 13)", duration: "3:47" },
      { id: "reclaim-14", track_title: "Reclaim (Track 14)", duration: "4:08" },
      { id: "reclaim-15", track_title: "Reclaim (Track 15)", duration: "4:31" }
    ]
  }
];

const CatalogPanel = () => {
  const [tracks, setTracks] = useState([]);
  const [albums, setAlbums] = useState(albumsData);
  const [viewMode, setViewMode] = useState('albums'); // 'albums' or 'tracks'
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    genre: '',
    mood: '',
    bpmMin: '',
    bpmMax: '',
    durationMin: '',
    durationMax: '',
    license: '',
  });

  // Load the catalog from the librosa API directly instead of broken todos endpoint
    const loadCatalog = async (params = {}) => {
      setIsLoading(true);
      try {
        console.log('🎵 CatalogPanel: Loading from API via service...');
        const { tracks: apiTracks } = await fetchCatalog(params);
        if (apiTracks.length > 0) {
          console.log('✅ CatalogPanel: Loaded', apiTracks.length, 'tracks');
          setTracks(apiTracks);
        } else {
          console.warn('⚠️ CatalogPanel: No tracks in API response');
          setTracks([]);
        }
      } catch (error) {
        console.error('❌ CatalogPanel: Error loading catalog:', error);
        setTracks([]);
      }
      setIsLoading(false);
    };

  // Client-side filtering function
  const getFilteredTracks = () => {
    return tracks.filter(track => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          (track.track_title || track.title || '').toLowerCase().includes(searchLower) ||
          (track.artist || '').toLowerCase().includes(searchLower) ||
          (track.album || '').toLowerCase().includes(searchLower) ||
          (track.genre || track.vibe || '').toLowerCase().includes(searchLower) ||
          (track.mood || '').toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Genre filter
      if (filters.genre) {
        const genreLower = filters.genre.toLowerCase();
        const trackGenre = (track.genre || track.vibe || '').toLowerCase();
        if (!trackGenre.includes(genreLower)) return false;
      }

      // Mood filter
      if (filters.mood) {
        const moodLower = filters.mood.toLowerCase();
        const trackMood = (track.mood || track.sync_sweet_spot || '').toLowerCase();
        if (!trackMood.includes(moodLower)) return false;
      }

      // BPM filters
      if (filters.bpmMin && track.bpm) {
        const bpmMin = parseInt(filters.bpmMin);
        if (track.bpm < bpmMin) return false;
      }
      if (filters.bpmMax && track.bpm) {
        const bpmMax = parseInt(filters.bpmMax);
        if (track.bpm > bpmMax) return false;
      }

      // Duration filters (convert track duration to minutes for comparison)
      if (filters.durationMin || filters.durationMax) {
        let trackDurationMinutes = 0;
        if (track.duration) {
          const parts = track.duration.split(':');
          if (parts.length === 2) {
            trackDurationMinutes = parseInt(parts[0]) + parseInt(parts[1]) / 60;
          }
        }

        if (filters.durationMin) {
          const minDuration = parseFloat(filters.durationMin);
          if (trackDurationMinutes < minDuration) return false;
        }
        if (filters.durationMax) {
          const maxDuration = parseFloat(filters.durationMax);
          if (trackDurationMinutes > maxDuration) return false;
        }
      }

      // License filter
      if (filters.license) {
        const trackLicense = track.license_type || track.license || '';
        if (trackLicense.toLowerCase() !== filters.license.toLowerCase()) return false;
      }

      return true;
    });
  };

  // Get filtered albums
  const getFilteredAlbums = () => {
    if (!filters.search && !filters.genre && !filters.mood && !filters.bpmMin && !filters.bpmMax && !filters.durationMin && !filters.durationMax && !filters.license) {
      return albums; // No filters, return all albums
    }

    return albums.map(album => {
      const filteredTracks = album.tracks?.filter(track => {
        // Apply same filtering logic to album tracks
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const matchesSearch = 
            (track.track_title || '').toLowerCase().includes(searchLower) ||
            (album.artist || '').toLowerCase().includes(searchLower) ||
            (album.title || '').toLowerCase().includes(searchLower);
          if (!matchesSearch) return false;
        }
        return true;
      }) || [];

      return {
        ...album,
        tracks: filteredTracks
      };
    }).filter(album => album.tracks.length > 0); // Only show albums with matching tracks
  };

  // Auto-load on mount
  useEffect(() => {
    loadCatalog();
  }, []);

  const handleSearch = () => {
    // Force re-render by updating a dummy state or just let the getFilteredTracks handle it
    // The filters are already updated in real-time, so we just need to trigger a re-render
    setViewMode(viewMode); // This will trigger a re-render without changing the actual view mode
  };

  const handleClear = () => {
    const cleared = {
      search: '',
      genre: '',
      mood: '',
      bpmMin: '',
      bpmMax: '',
      durationMin: '',
      durationMax: '',
      license: '',
    };
    setFilters(cleared);
  };

  // Simple placeholder actions for catalog buttons
  const handleAddToCatalog = () => alert('Add to Catalog coming soon');
  const handleSubmitForSync = () => alert('Submit for Sync coming soon');
  const handleExportPDF = () => alert('Export to PDF coming soon');

  const playAll = () => {
    if (viewMode === 'albums') {
      // Play all tracks from all albums
      const allTracks = albums.flatMap(album => album.tracks || []);
      const clips = allTracks.map((t) => t.assets?.clip_wav || t.links?.mp3).filter(Boolean);
      if (clips.length === 0) return;
      
      let idx = 0;
      const playNext = () => {
        if (idx >= clips.length) return;
        const audio = new Audio(clips[idx]);
        idx += 1;
        audio.onended = playNext;
        audio.play().catch(console.error);
      };
      playNext();
    } else {
      // Original track playing logic
      const clips = tracks.map((t) => t.links?.mp3).filter(Boolean);
      let idx = 0;
      const playNext = () => {
        if (idx >= clips.length) return;
        const audio = new Audio(clips[idx]);
        idx += 1;
        audio.onended = playNext;
        audio.play();
      };
      playNext();
    }
  };

  // Get the data to display based on filters
  const filteredTracks = getFilteredTracks();
  const filteredAlbums = getFilteredAlbums();
  const displayTracks = viewMode === 'tracks' ? filteredTracks : [];
  const displayAlbums = viewMode === 'albums' ? filteredAlbums : [];

  if (isLoading) {
    return (
      <div className="professional-container">
        <div className="professional-loading">
          Loading catalog...
        </div>
      </div>
    );
  }

  return (
    <div className="professional-container">
      {/* Professional Header */}
      <div className="professional-header">
        <h1>🎵 Music Catalog Management</h1>
        <p>Professional catalog for Rue de Vivre • {tracks.length} tracks across {albums.length} albums</p>
        {(filteredTracks.length !== tracks.length || filteredAlbums.length !== albums.length) && (
          <p style={{ color: 'var(--accent-color)', fontSize: '0.9rem', marginTop: '0.5rem' }}>
            Showing {viewMode === 'albums' ? filteredAlbums.length : filteredTracks.length} filtered results
          </p>
        )}
      </div>

      {/* Album Overview - Horizontal Layout */}
      <div className="professional-card" style={{ marginBottom: '2rem' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '200px 1fr auto', 
          gap: '2rem', 
          alignItems: 'center' 
        }}>
          {/* Album Cover */}
          <div>
            <img
              src={albums[0]?.cover || '/logo.svg'}
              alt="Album Cover"
              style={{
                width: '200px',
                height: '200px',
                borderRadius: '12px',
                objectFit: 'cover',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}
            />
          </div>
          
          {/* Album Info */}
          <div>
            {(() => {
              const artistName = albums[0]?.artist || '';
              const totalTracks = albums.reduce((total, album) => total + (album.tracks?.length || 0), 0);
              const catalogTitle = albums.length > 1 ? `${artistName} Catalog` : albums[0]?.title;
              return (
                <>
                  <h2 style={{
                    fontSize: '2rem',
                    fontWeight: '700',
                    color: 'var(--accent-color)',
                    margin: '0 0 0.5rem 0'
                  }}>
                    {catalogTitle}
                  </h2>
                  {artistName && (
                    <p style={{
                      fontSize: '1.2rem',
                      color: 'var(--text-secondary)',
                      margin: '0 0 1rem 0'
                    }}>
                      by {artistName}
                    </p>
                  )}
                  {albums.length > 1 ? (
                    <div style={{
                      fontSize: '0.9rem',
                      color: 'var(--text-secondary)'
                    }}>
                      {albums.length} Album{albums.length !== 1 ? 's' : ''} • {totalTracks} Tracks
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', gap: '2rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                        <span>Released: {albums[0]?.releaseDate}</span>
                        <span>ASCAP: {albums[0]?.ascap_work_id}</span>
                      </div>
                      <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                        {(albums[0]?.tracks?.length || 0)} Tracks
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <button
              onClick={playAll}
              className="professional-button"
              style={{ padding: '1rem 2rem' }}
            >
              ▶️ Play All
            </button>
          </div>
        </div>
      </div>

      {/* View Mode Controls & Stats - Horizontal Layout */}
      <div className="professional-grid-2" style={{ marginBottom: '2rem' }}>
        {/* View Controls */}
        <div className="professional-card">
          <div className="professional-card-header">
            <h3 className="professional-card-title">📋 View Mode</h3>
          </div>
          <div className="professional-card-content">
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => setViewMode('albums')}
                className={`professional-button ${viewMode === 'albums' ? '' : 'professional-button-secondary'}`}
                style={{ flex: 1 }}
              >
                📀 Albums
              </button>
              <button
                onClick={() => setViewMode('tracks')}
                className={`professional-button ${viewMode === 'tracks' ? '' : 'professional-button-secondary'}`}
                style={{ flex: 1 }}
              >
                🎵 All Tracks
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="professional-card">
          <div className="professional-card-header">
            <h3 className="professional-card-title">📊 Catalog Stats</h3>
          </div>
          <div className="professional-card-content">
            <div className="professional-grid-2">
              <div className="professional-metric-card">
                <div className="professional-metric-value">
                  {viewMode === 'albums' ? filteredAlbums.reduce((total, album) => total + (album.tracks?.length || 0), 0) : filteredTracks.length}
                </div>
                <div className="professional-metric-label">
                  {viewMode === 'albums' ? 'Total Tracks' : 'Filtered Tracks'}
                </div>
              </div>
              <div className="professional-metric-card">
                <div className="professional-metric-value">
                  {viewMode === 'albums' ? filteredAlbums.length : albums.length}
                </div>
                <div className="professional-metric-label">
                  {viewMode === 'albums' ? 'Filtered Albums' : 'Total Albums'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area - Professional Grid Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '2rem' }}>
        {/* Filters Sidebar */}
        <div className="professional-card">
          <div className="professional-card-header">
            <h3 className="professional-card-title">🔍 Filters</h3>
          </div>
          <div className="professional-card-content">
            <FilterSidebar
              filters={filters}
              onChange={(newFilters) => {
                setFilters(newFilters);
                // Real-time filtering - no need to call handleSearch
              }}
              onSearch={handleSearch}
              onClear={handleClear}
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="professional-card">
          <div className="professional-card-header">
            <h3 className="professional-card-title">
              {viewMode === 'albums' ? '📀 Albums' : '🎵 All Tracks'}
            </h3>
          </div>
          <div className="professional-card-content">
            {viewMode === 'albums' ? (
              <AlbumGrid albums={displayAlbums} />
            ) : (
              <TrackGrid tracks={displayTracks} onTrackDeleted={() => loadCatalog()} />
            )}
          </div>
        </div>
      </div>

      {/* Action Footer */}
      <div className="professional-card" style={{ marginTop: '2rem' }}>
        <div className="professional-card-content">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <button className="professional-button" onClick={handleAddToCatalog}>
                ➕ Add to Catalog
              </button>
              <button className="professional-button" onClick={handleSubmitForSync}>
                🎯 Submit for Sync
              </button>
              <button className="professional-button" onClick={handleExportPDF}>
                📄 Export to PDF
              </button>
              <a
                href="#/catalog/simple"
                className="professional-button professional-button-secondary"
                style={{ textDecoration: 'none' }}
              >
                📊 Simple Catalog View
              </a>
            </div>
            <div style={{ 
              fontSize: '0.9rem', 
              color: 'var(--text-secondary)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>🌐</span>
              <a 
                href="https://decodedmusic.com" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: 'var(--accent-color)', textDecoration: 'none' }}
              >
                www.decodedmusic.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogPanel;
